import clsx from "clsx";

import { GroupNS } from "@smart/bridge-types-basic";
import { ProgressBar, SortableGroup } from "@smart/itops-sb-design-system-dom";
import {
  getDuplicatedTitle,
  rankBetween,
  removeKeys,
} from "@smart/itops-utils-basic";
import { useDeleteGroup, useUpdateGroup } from "@smart/manage-gql-client-dom";

import { GroupCard } from "./group-part";
import { LanguageKey, baseLanguageKey } from "../../../../types";
import { DroppableSeparator } from "../../drag-and-drop";
import {
  ConditionActions,
  FieldTemplate,
  GqlField,
  GqlFieldValues,
  GqlGroup,
  GqlGroupValues,
  GqlMatterLayout,
  GqlMatterType,
  GqlSectionItem,
  LoadMatterFields,
  OnDeleteField,
  OnFieldTypeChange,
  OnUpdateField,
  QuestionEditMode,
} from "../../types";
import {
  buildContactDetailsRoleChange,
  ChangeStatus,
  EntityChangesMap,
  getContactDetailsType,
} from "../../utils";
import { MappedFieldsOptions } from "../mapped-fields-modal";
import { buildContactDetailsFixedSelection } from "../mapped-fields-modal/helpers";

type GroupProps = {
  index: number;
  mode?: QuestionEditMode;
  item: GqlGroupValues;
  fields: GqlFieldValues[];
  updateGroup: ReturnType<typeof useUpdateGroup>[0];
  deleteGroup: ReturnType<typeof useDeleteGroup>[0];
  duplicateGroup: ({
    groupToCopyUri,
    newGroupTitle,
    newGroupOrder,
  }: {
    groupToCopyUri: string;
    newGroupTitle?: string;
    newGroupOrder: string;
  }) => Promise<void>;
  sectionItems: GqlSectionItem[];
  isEditing?: boolean;
  isDragging?: boolean;
  loading?: boolean;
  onUpdateField: OnUpdateField;
  onDeleteField: OnDeleteField;
  matterLayouts?: GqlMatterLayout[];
  matterTypes?: GqlMatterType[];
  loadMatterFields: LoadMatterFields;
  updatingItems: Record<string, boolean | undefined>;
  editingItemUri?: string;
  conditionActions: ConditionActions;
  onFieldTypeChange: OnFieldTypeChange;
  excludingFieldTemplates: FieldTemplate[];
  lastActiveIds: string[];
  viewingLanguage: LanguageKey;
  hasSubmissionLinks?: boolean;
  fieldChangesMap?: EntityChangesMap;
  changeStatus?: ChangeStatus;
  openMappedFieldsModal?: (options: MappedFieldsOptions) => void;
  formFields: GqlField[];
  formGroups: GqlGroup[];
};

export const Group = ({
  item: group,
  fields,
  index,
  mode,
  loading,
  isEditing,
  isDragging,
  matterTypes,
  deleteGroup,
  updateGroup,
  duplicateGroup,
  sectionItems,
  onUpdateField,
  onDeleteField,
  matterLayouts,
  editingItemUri,
  updatingItems,
  loadMatterFields,
  conditionActions,
  onFieldTypeChange,
  excludingFieldTemplates,
  lastActiveIds,
  viewingLanguage,
  hasSubmissionLinks,
  fieldChangesMap,
  changeStatus,
  openMappedFieldsModal,
  formFields,
  formGroups,
}: GroupProps) => {
  const onDeleteGroup = async () => {
    await deleteGroup({
      variables: {
        sectionUri: group.sectionUri,
        uri: group.uri,
        formUri: group.formUri,
      },
    });
  };

  const groupContainsPaymentField = fields.some(
    (field) => field.type === "payment",
  );

  const onUpdateGroup = async ({
    label,
    description,
    repeatable,
    repeatPrompt,
  }: {
    label?: string;
    description?: string;
    repeatable?: boolean;
    repeatPrompt?: string;
  }) => {
    if (groupContainsPaymentField && repeatable) {
      console.error("repeatable group cannot contain a payment field");
      return;
    }
    await updateGroup({
      variables: {
        uri: group.uri,
        formUri: group.formUri,
        sectionUri: group.sectionUri,
        order: group.order,
        forLanguage:
          viewingLanguage === baseLanguageKey ? undefined : viewingLanguage,
        fields: {
          description: description ?? (group.description || undefined),
          allowedRepeatable: !!group.allowedRepeatable,
          field: group.field
            ? {
                ...removeKeys(group.field, [
                  "__typename",
                  "displayName",
                  "details",
                ]),
                possibleValues: group.field.possibleValues || undefined,
                dataSourceMapping: group.field.dataSourceMapping || undefined,
                originalName: group.field.originalName || undefined,
              }
            : undefined,
          hint: group.hint,
          label: label ?? group.label,
          layout: group.layout
            ? {
                ...removeKeys(group.layout, ["__typename", "displayName"]),
                parentId: group.layout.parentId || undefined,
                parentName: group.layout.parentName || undefined,
                parentProviderId: group.layout.parentProviderId || undefined,
              }
            : undefined,
          links: group.links
            ? group.links.map((link) => ({
                ...removeKeys(link, ["__typename"]),
                fieldUri: link.fieldUri,
              }))
            : undefined,
          maxRepeat: group.maxRepeat || undefined,
          minRepeat: group.minRepeat || undefined,
          repeatPrompt: repeatPrompt ?? (group.repeatPrompt || undefined),
          repeatable:
            repeatable !== undefined ? repeatable : !!group.repeatable,
          templateType: group.templateType || undefined,
          type: group.type,
        },
      },
    });
  };

  const onDuplicateGroup = async () => {
    await duplicateGroup({
      groupToCopyUri: group.uri,
      newGroupTitle: getDuplicatedTitle(
        group.label || "",
        sectionItems
          .filter((si) => GroupNS.isUriMatch(si.uri))
          .map((g) => g.label)
          .filter(Boolean) as string[],
      ),
      newGroupOrder: rankBetween({
        after: group.order,
        before: sectionItems[index + 1]?.order,
      }),
    });
  };

  const onChangeContactRole = async (roleLayout: GqlMatterLayout) => {
    const { label, layout, fieldsToRemap } = buildContactDetailsRoleChange({
      group,
      fields,
      roleLayout,
    });
    const contactType = getContactDetailsType(group.field);
    if (!contactType) return;

    await updateGroup({
      variables: {
        uri: group.uri,
        formUri: group.formUri,
        sectionUri: group.sectionUri,
        order: group.order,
        forLanguage:
          viewingLanguage === baseLanguageKey ? undefined : viewingLanguage,
        fields: {
          description: group.description || undefined,
          allowedRepeatable: !!group.allowedRepeatable,
          hint: group.hint,
          label,
          layout,
          field: group.field
            ? {
                ...removeKeys(group.field, [
                  "__typename",
                  "displayName",
                  "details",
                ]),
                possibleValues: group.field.possibleValues || undefined,
                dataSourceMapping: group.field.dataSourceMapping || undefined,
                originalName: group.field.originalName || undefined,
              }
            : undefined,
          links: group.links
            ? group.links.map((link) => ({
                ...removeKeys(link, ["__typename"]),
                fieldUri: link.fieldUri,
              }))
            : undefined,
          maxRepeat: group.maxRepeat || undefined,
          minRepeat: group.minRepeat || undefined,
          repeatPrompt: group.repeatPrompt || undefined,
          repeatable: !!group.repeatable,
          templateType: group.templateType || undefined,
          type: group.type,
        },
      },
    });

    await Promise.all(
      fieldsToRemap.map((mappedField) =>
        onUpdateField({
          field: mappedField,
          updated: {
            layout: {
              id: roleLayout.id,
              name: roleLayout.name,
              providerId: roleLayout.providerId,
              parentId: roleLayout.parentId ?? null,
              parentName: roleLayout.parentName ?? null,
              parentProviderId: roleLayout.parentProviderId ?? null,
            },
          },
        }),
      ),
    );
  };

  const onAddAndRemoveContactFields = () => {
    const contactType = getContactDetailsType(group.field);
    const layout =
      matterLayouts?.find((item) => item.id === group.layout?.id) ||
      (group.layout as GqlMatterLayout | null | undefined);
    if (!contactType || !layout || !openMappedFieldsModal) return;

    openMappedFieldsModal({
      destinationGroupUri: group.uri,
      afterPosition: fields[fields.length - 1]?.order,
      fixedSelection: buildContactDetailsFixedSelection({
        layout,
        contactType,
      }),
    });
  };

  return (
    <div>
      <SortableGroup
        id={group.uri}
        subItemIds={fields.map((field) => field.uri)}
      >
        {({ dragHandleProps, isDragging: isDraggingFromSortable }) => (
          <>
            <GroupCard
              className={clsx(isDraggingFromSortable && "opacity-50")}
              group={group}
              mode={mode}
              loading={loading}
              editing={isEditing}
              isDragging={isDragging || isDraggingFromSortable}
              matterTypes={matterTypes}
              groupFields={fields}
              onUpdateField={onUpdateField}
              onDeleteField={onDeleteField}
              onDuplicateGroup={onDuplicateGroup}
              matterLayouts={matterLayouts}
              onDeleteGroup={onDeleteGroup}
              onUpdateGroup={onUpdateGroup}
              onChangeContactRole={onChangeContactRole}
              editingItemUri={editingItemUri}
              loadMatterFields={loadMatterFields}
              updatingItems={updatingItems}
              dragHandleProps={dragHandleProps}
              conditionActions={conditionActions}
              onFieldTypeChange={onFieldTypeChange}
              excludingFieldTemplates={excludingFieldTemplates}
              lastActiveIds={lastActiveIds}
              groupContainsPaymentField={groupContainsPaymentField}
              viewingLanguage={viewingLanguage}
              hasSubmissionLinks={hasSubmissionLinks}
              fieldChangesMap={fieldChangesMap}
              changeStatus={changeStatus}
              onAddAndRemoveContactFields={onAddAndRemoveContactFields}
              formFields={formFields}
              formGroups={formGroups}
            />
            {loading && (
              <ProgressBar className="h-[0.3rem] max-w-[90rem] m-auto" />
            )}
            <DroppableSeparator
              index={index + 1}
              disabled={mode === "reordering"}
            />
          </>
        )}
      </SortableGroup>
    </div>
  );
};
